import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileText, ImageUp, Plus, RefreshCw, X } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { formatFileSize } from "../../utils/uploadLimits";
import {
  AppButton,
  DropZone,
  FieldError,
  FileViewerModal,
  HelpText,
  IconButton,
  Label,
  ProgressBar,
  cn,
} from "../ui";

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

/**
 * UploadWithS3 — pick a file, put it in storage, hand the key back.
 *
 * The call site stores the KEY. `onUpload` gets a string in single mode and the
 * full array in multiple mode, matching what the two fee screens already expect.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 * A bare `<input type="file">` — the browser's own "Choose File / No file
 * chosen" chrome, which is the one control in the app that could never follow
 * the theme, the brand or the control spec — plus a react-bootstrap
 * `ProgressBar`, an `Image` thumbnail and a red `Delete` button.
 *
 * Three things it could not do, all of which matter for a payment proof:
 *
 *   • **Show what is already attached.** The preview lived in local state built
 *     from `URL.createObjectURL`, so a form opened in EDIT mode — the fee form
 *     loading `txn.refImage` — rendered an empty picker beside a file that was
 *     really there. Existing keys are presigned and shown now.
 *   • **Take a paste.** A payment proof is a screenshot, and the first thing
 *     anyone tries is Ctrl+V.
 *   • **Let you read it.** A UTR number in an 80px thumbnail is unreadable;
 *     the image opens in `FileViewerModal`, which zooms and rotates.
 *
 * Props:
 *   s3KeyPrefix — ⚠️ accepted and NOT sent. `POST /files/upload` builds an
 *                 institute-scoped key from `entity`/`entityId`/`type`, and its
 *                 `keyPrefix` branch bypasses that scoping entirely. Passing
 *                 this through would put tenant files on an unscoped path, so
 *                 it stays inert exactly as it has been.
 *   accept      — accept string (default "image/*")
 *   value       — the stored key (string), or keys (array) when `multiple`
 *   onUpload    — (key | keys) => void
 *   onDelete    — (removedKey) => void
 *   multiple    — allow several files (default false)
 *   maxSizeMB   — per-file ceiling (default 10)
 *   disabled    — no picking, no removing
 *   label       — the field label. Rendered here and bound with
 *                 `aria-labelledby`, the same wiring `ui/FileUpload` uses.
 *                 ⚠️ Do NOT wrap this control in a `FormField` instead: that
 *                 generates an id and clones its child to inject it, and this
 *                 component is a composite with no single control to take it —
 *                 the label's `htmlFor` would point at nothing.
 *   hint        — help text under the label
 */
const UploadWithS3 = ({
  s3KeyPrefix,
  accept = "image/*",
  onUpload,
  onDelete,
  value,
  multiple = false,
  maxSizeMB = 10,
  disabled = false,
  label,
  hint,
  className = "",
}) => {
  const fieldId = React.useId();
  const labelId = label ? `${fieldId}-label` : undefined;
  const hintId = hint ? `${fieldId}-hint` : undefined;

  // One row per attachment: { key, url, name, type, uploading, progress }
  // `key` is null only while the upload is still in flight.
  const [items, setItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null);
  const inputRef = useRef(null);

  /* The ref is the authoritative list; `items` is the copy React renders.
     Two reasons it has to work this way:
       • A `setItems` UPDATER does not run when it is called — it runs during the
         next render — so the new array cannot be read back on the line after,
         and an upload that published from one would report an empty key.
       • The reconcile effect below must see the current rows WITHOUT depending
         on them, or it re-runs on its own result and re-presigns a key this
         component uploaded moments ago.
     Every write goes through `setRows`, which returns the committed array. */
  const itemsRef = useRef(items);
  const setRows = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(itemsRef.current) : updater;
    itemsRef.current = next;
    setItems(next);
    return next;
  }, []);

  /* The effect below keys on the CONTENTS, not the prop. In multiple mode
     `value` is a fresh array on every render, so depending on it directly would
     re-run this — and re-presign every attachment — on each keystroke elsewhere
     in the form. */
  const valueKey = asArray(value).join("|");

  const wantsImage = accept.startsWith("image");
  const maxBytes = maxSizeMB * 1024 * 1024;
  const atCapacity = !multiple && items.length > 0;

  /* ── Resolve keys the parent owns that we have no preview for ──────────────
     This is what makes an edit form show the proof it already has. Blob URLs
     from this session win, so re-presigning never flickers a preview we just
     made locally. */
  useEffect(() => {
    const keys = valueKey ? valueKey.split("|") : [];
    let cancelled = false;

    // Drop rows whose key the parent no longer holds, and keep everything still
    // uploading (it has no key yet, so `keys` cannot mention it).
    setRows((prev) => {
      const kept = prev.filter((it) => it.uploading || keys.includes(it.key));
      return kept.length === prev.length ? prev : kept;
    });

    const missing = keys.filter((k) => k && !itemsRef.current.some((it) => it.key === k));
    if (!missing.length) return undefined;

    (async () => {
      const resolved = await Promise.all(
        missing.map(async (key) => {
          try {
            const { data } = await apiClient.get(
              `/files/presign-get?key=${encodeURIComponent(key)}`
            );
            return { key, url: data.url, name: key.split("/").pop(), uploading: false };
          } catch {
            // A key we cannot presign still exists — show it as a plain row
            // rather than pretending nothing is attached.
            return { key, url: "", name: key.split("/").pop(), uploading: false };
          }
        })
      );
      if (!cancelled) {
        setRows((prev) => [
          ...prev,
          ...resolved.filter((r) => !prev.some((p) => p.key === r.key)),
        ]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [valueKey, setRows]);

  const publish = useCallback(
    (rows) => {
      const keys = rows.map((r) => r.key).filter(Boolean);
      onUpload?.(multiple ? keys : keys[keys.length - 1] || "");
    },
    [multiple, onUpload]
  );

  const uploadOne = useCallback(
    async (file, replacing) => {
      const localUrl = URL.createObjectURL(file);
      const rowId = `${file.name}-${Date.now()}-${Math.random()}`;
      const row = {
        rowId,
        key: null,
        url: localUrl,
        name: file.name,
        type: file.type,
        uploading: true,
        progress: 0,
      };
      setRows((prev) => (replacing ? [row] : [...prev, row]));

      try {
        const body = new FormData();
        body.append("file", file);
        const { data } = await apiClient.post("/files/upload", body, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            setRows((prev) =>
              prev.map((it) => (it.rowId === rowId ? { ...it, progress: pct } : it))
            );
          },
        });
        const next = setRows((prev) =>
          prev.map((it) =>
            it.rowId === rowId ? { ...it, key: data.key, uploading: false, progress: 100 } : it
          )
        );
        publish(next);
      } catch {
        setError(`Couldn't upload “${file.name}”. Try again.`);
        setRows((prev) => prev.filter((it) => it.rowId !== rowId));
        URL.revokeObjectURL(localUrl);
      }
    },
    [publish, setRows]
  );

  const accepted = useCallback(
    (file) => {
      if (wantsImage && !file.type.startsWith("image/")) {
        setError("That is not an image. Choose a JPG, PNG or WebP.");
        return false;
      }
      if (file.size > maxBytes) {
        setError(
          `“${file.name}” is ${formatFileSize(file.size)} — the limit is ${maxSizeMB} MB.`
        );
        return false;
      }
      return true;
    },
    [wantsImage, maxBytes, maxSizeMB]
  );

  const addFiles = useCallback(
    async (files) => {
      if (disabled || !files.length) return;
      setError("");

      if (!multiple) {
        const file = files[0];
        if (!accepted(file)) return;
        // Single mode replaces. Clear the old object out of storage first so an
        // abandoned proof does not outlive the record it belonged to.
        const previous = items.find((it) => it.key);
        if (previous?.key) {
          apiClient.delete("/files/delete", { data: { key: previous.key } }).catch(() => {});
          onDelete?.(previous.key);
        }
        await uploadOne(file, true);
        return;
      }

      for (const file of files) {
        if (accepted(file)) await uploadOne(file, false);
      }
    },
    [disabled, multiple, items, accepted, uploadOne, onDelete]
  );

  const removeItem = async (item) => {
    if (disabled || item.uploading) return;
    setError("");
    const remaining = items.filter((it) => it.key !== item.key);
    setRows(remaining);
    // Single mode has always reported a removal through `onDelete` alone;
    // republishing an empty key on top of it would tell the parent the same
    // thing twice, in two shapes. The array in multiple mode does have to be
    // resent, because removing one entry rewrites it.
    if (multiple) publish(remaining);
    if (!item.key) return;
    try {
      await apiClient.delete("/files/delete", { data: { key: item.key } });
      onDelete?.(item.key);
    } catch {
      setError("Removed here, but the stored copy could not be deleted.");
      onDelete?.(item.key);
    }
  };

  const onDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  /* Paste lands here because the drop zone is a real <button>: it takes focus,
     and the paste event bubbles to this wrapper. Scoped to the control rather
     than the document so it cannot steal a paste meant for a nearby textarea. */
  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    addFiles(files);
  };

  const openPicker = () => inputRef.current?.click();

  const zoneProps = {
    disabled,
    "aria-labelledby": labelId,
    "aria-describedby": hintId,
    onClick: openPicker,
    onDragEnter: onDrag,
    onDragOver: onDrag,
    onDragLeave: onDrag,
    onDrop,
  };

  return (
    <div
      className={cn("tw-flex tw-w-full tw-flex-col tw-gap-1.5", className)}
      role="group"
      aria-labelledby={labelId}
      onPaste={onPaste}
    >
      {label && <Label id={labelId}>{label}</Label>}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          addFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
        className="tw-hidden"
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />

      {items.length === 0 ? (
        <DropZone state={dragActive ? "active" : "idle"} {...zoneProps}>
          <ImageUp
            size={22}
            aria-hidden="true"
            className="tw-text-content-subtle group-hover:tw-text-primary"
          />
          <span className="tw-text-center tw-text-[0.8125rem] tw-font-medium tw-text-content">
            Drop {wantsImage ? "an image" : "a file"} here, paste it, or click to browse
          </span>
          <HelpText className="tw-text-center">
            {wantsImage ? "JPG, PNG or WebP" : "Any file"} · up to {maxSizeMB} MB
          </HelpText>
        </DropZone>
      ) : (
        <div className="tw-flex tw-flex-wrap tw-gap-2.5">
          {items.map((item) => (
            <Thumb
              key={item.key || item.rowId}
              item={item}
              isImage={wantsImage}
              disabled={disabled}
              onOpen={() => item.url && !item.uploading && setViewing(item)}
              onRemove={() => removeItem(item)}
            />
          ))}

          {multiple && (
            // The spread comes FIRST so the name below wins. `aria-labelledby`
            // outranks `aria-label`, so leaving the group's label in place here
            // would make this button announce as the field, not as "add".
            <DropZone
              size="sm"
              state={dragActive ? "active" : "idle"}
              className="tw-h-28 tw-w-28 tw-flex-col"
              {...zoneProps}
              aria-labelledby={undefined}
              aria-label="Add another file"
            >
              <Plus size={18} aria-hidden="true" className="tw-text-content-subtle" />
              <span className="tw-text-xs tw-text-content-muted">Add</span>
            </DropZone>
          )}

          {atCapacity && !disabled && (
            <AppButton
              variant="outline"
              size="sm"
              className="tw-self-center"
              onClick={openPicker}
            >
              <RefreshCw size={16} /> Replace
            </AppButton>
          )}
        </div>
      )}

      {hint && !error && <HelpText id={hintId}>{hint}</HelpText>}
      {error && <FieldError>{error}</FieldError>}

      <FileViewerModal
        show={!!viewing}
        onHide={() => setViewing(null)}
        fileUrl={viewing?.url}
        fileName={viewing?.name}
        fileType={viewing?.type || (wantsImage ? "image/png" : "")}
      />
    </div>
  );
};

/**
 * One attachment tile — the preview, its progress, and remove.
 *
 * The preview is a `<button>` rather than an image with a click handler: it
 * opens a dialog, which is a button's job, and it brings Enter, Space and the
 * focus ring with it.
 */
const Thumb = ({ item, isImage, disabled, onOpen, onRemove }) => (
  <div className="tw-relative tw-h-28 tw-w-28 tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-alt">
    <button
      type="button"
      onClick={onOpen}
      disabled={item.uploading || !item.url}
      className="tw-h-full tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 disabled:tw-cursor-default tw-focus-ring"
      title={item.name}
    >
      <span className="tw-sr-only">
        {item.uploading ? `Uploading ${item.name}` : `View ${item.name}`}
      </span>
      {isImage && item.url ? (
        <img
          src={item.url}
          alt=""
          className={cn(
            "tw-h-full tw-w-full tw-object-cover tw-transition-opacity",
            item.uploading && "tw-opacity-50"
          )}
        />
      ) : (
        <span className="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-px-2">
          <FileText size={20} className="tw-text-content-subtle" aria-hidden="true" />
          <span className="tw-w-full tw-truncate tw-text-center tw-text-xs tw-text-content-muted">
            {item.name}
          </span>
        </span>
      )}
    </button>

    {/* The progress strip is a SOLID surface, not `/90`: the `surface` tokens
        are hex, so they carry no `<alpha-value>` and an alpha suffix on one
        emits no CSS at all — `verify:tw` catches it, nothing at runtime would. */}
    {item.uploading && (
      <span className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-flex tw-flex-col tw-gap-1 tw-bg-surface-raised tw-px-1.5 tw-py-1">
        <ProgressBar value={item.progress} />
        <span className="tw-text-center tw-text-[0.6875rem] tw-font-semibold tw-text-content-muted">
          {item.progress}%
        </span>
      </span>
    )}

    {!item.uploading && !disabled && (
      <span className="tw-absolute tw-right-1 tw-top-1">
        <IconButton
          label={`Remove ${item.name}`}
          icon={X}
          size="xs"
          variant="destructive"
          onClick={onRemove}
        />
      </span>
    )}
  </div>
);

export default UploadWithS3;
